import { useState } from "react";
import { api, type Booking, type Client, type Package } from "../../lib/api";
import { formatPrice, toLocalDatetime, fromEUR } from "../../lib/format";
import { Modal } from "../ui/Modal";



// ─── Edit modal ───────────────────────────────────────────────────────────────

export function EditModal({ booking, clients, packages, onClose, onSaved }: {
  booking: Booking;
  clients: Client[];
  packages: Package[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editTitle, setEditTitle] = useState(booking.Title);
  const [editClientID, setEditClientID] = useState(booking.ClientID);
  const [editPackageID, setEditPackageID] = useState(booking.PackageID ?? "");
  const [editLocation, setEditLocation] = useState(booking.Location);
  const [editScheduledAt, setEditScheduledAt] = useState(toLocalDatetime(booking.ScheduledAt));
  const [editPriceEUR, setEditPriceEUR] = useState(fromEUR(booking.PriceCents));
  const [editDepositEUR, setEditDepositEUR] = useState(fromEUR(booking.DepositCents));
  const [editPackageName, setEditPackageName] = useState(booking.PackageName);
  const [editNotes, setEditNotes] = useState(booking.Notes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const priceCents = editPriceEUR ? Math.round(parseFloat(editPriceEUR.replace(",", ".")) * 100) : 0;
      const depositCents = editDepositEUR ? Math.round(parseFloat(editDepositEUR.replace(",", ".")) * 100) : 0;
      const scheduledISO = editScheduledAt ? new Date(editScheduledAt).toISOString() : "";
      await api.updateBooking(booking.ID, {
        title: editTitle,
        location: editLocation,
        scheduled_at: scheduledISO,
        price_cents: priceCents,
        deposit_cents: depositCents,
        currency: booking.Currency || "EUR",
        package_name: editPackageName,
        package_id: editPackageID || null,
        notes: editNotes,
        deposit_paid: booking.DepositPaid,
        balance_paid: booking.BalancePaid,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // When a package is selected, pre-fill name and price
  function handlePackageSelect(pkgID: string) {
    setEditPackageID(pkgID);
    if (pkgID) {
      const pkg = packages.find(p => p.ID === pkgID);
      if (pkg) {
        setEditPackageName(pkg.Name);
        setEditPriceEUR((pkg.PriceCents / 100).toFixed(2));
      }
    }
  }

  return (
    <Modal open onClose={onClose} title="Edit booking" maxWidth={560}>
      {error && <div className="alert alert-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
      <form onSubmit={handleSave}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div className="form-group">
              <label>Title *</label>
              <input required value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Client</label>
              <select value={editClientID} onChange={(e) => setEditClientID(e.target.value)}>
                <option value="">— none —</option>
                {clients.map((c) => (
                  <option key={c.ID} value={c.ID}>{c.DisplayName}</option>
                ))}
              </select>
            </div>
          </div>
          {packages.length > 0 && (
            <div className="form-group">
              <label>Package</label>
              <select value={editPackageID} onChange={e => handlePackageSelect(e.target.value)}>
                <option value="">— No package (custom) —</option>
                {packages.map(p => (
                  <option key={p.ID} value={p.ID}>{p.Name} — {formatPrice(p.PriceCents, p.Currency)}</option>
                ))}
              </select>
              <small style={{ color: "var(--muted)" }}>Select a package to pre-fill name and price, or leave blank for a custom booking.</small>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div className="form-group">
              <label>Location</label>
              <input value={editLocation} onChange={(e) => setEditLocation(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Scheduled</label>
              <input type="datetime-local" value={editScheduledAt} onChange={(e) => setEditScheduledAt(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Price (EUR)</label>
              <input type="number" step="0.01" value={editPriceEUR} onChange={(e) => setEditPriceEUR(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Deposit (EUR)</label>
              <input type="number" step="0.01" value={editDepositEUR} onChange={(e) => setEditDepositEUR(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Package name</label>
              <input value={editPackageName} onChange={(e) => setEditPackageName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea rows={2} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
          </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

